import React from 'react';
import PropTypes from 'prop-types';
import ABBPropTypes from 'airbnb-prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import ButtonBase from './ButtonBase';
import { StackWrap } from '../StackView';
import Fieldset from '../Fieldset';
import { useViewport } from '../ViewportProvider';
import { useThemeTokens, useThemeTokensCallback } from '../ThemeProvider';
import { a11yProps, containUniqueFields, focusHandlerProps, pressProps, getTokensPropType, selectSystemProps, selectTokens, useMultipleInputValues, variantProp, viewProps } from '../utils';
import { getPressHandlersWithArgs } from '../utils/pressability';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const [selectItemProps, selectedItemPropTypes] = selectSystemProps([a11yProps, focusHandlerProps, pressProps, viewProps]);
const getStackWrapTokens = variant => {
  return Platform.select({
    web: {
      justifyContent: variant?.width === 'equal' ? 'space-evenly' : 'flex-start',
      width: variant?.width === 'equal' ? '100%' : 'auto'
    },
    default: {}
  });
};
const ButtonGroup = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    variant,
    buttonVariant,
    tokens,
    items = [],
    values,
    initialValues,
    maxValues = 1,
    onChange,
    readOnly = false,
    inactive = false,
    legend,
    tooltip,
    hint,
    hintPosition = 'inline',
    validation,
    feedback,
    name: inputGroupName,
    copy,
    iconPosition,
    accessibilityRole = maxValues === 1 ? 'radiogroup' // radiogroup is cross-platform; only web aria has generic groups
    : Platform.select({
      web: 'group',
      default: 'none'
    }),
    ...rest
  } = _ref;
  const viewport = useViewport();
  const themeTokens = useThemeTokens('ButtonGroup', tokens, variant, {
    viewport
  });
  const themeStackTokens = selectTokens('StackView', themeTokens);
  const variantStackTokens = getStackWrapTokens(variant);
  const stackTokens = {
    ...themeStackTokens,
    ...variantStackTokens
  };
  const {
    direction,
    space,
    fieldSpace,
    borderRadius,
    backgroundColor,
    padding,
    gap
  } = themeTokens;
  const themeButtonTokensCallback = useThemeTokensCallback('ButtonGroupItem', tokens, variant);
  const getButtonTokens = state => {
    const themeButtonTokens = themeButtonTokensCallback(state);
    return {
      ...themeButtonTokens,
      width: variant?.width === 'equal' ? '100%' : 'auto',
      flex: variant?.width === 'equal' ? 1 : undefined
    };
  };
  const {
    currentValues,
    toggleOneValue
  } = useMultipleInputValues({
    initialValues,
    values,
    maxValues,
    onChange,
    readOnly
  });
  const systemProps = selectProps({
    accessibilityRole,
    ...rest
  });
  const itemA11yRole = systemProps.accessibilityRole === 'radiogroup' ? 'radio' : 'checkbox';
  const uniqueFields = ['id', 'label'];
  if (!containUniqueFields(items, uniqueFields)) {
    throw new Error(`ButtonGroup items must have unique ${uniqueFields.join(', ')}`);
  }

  // Some web screenreaders e.g. MacOS Voiceover don't handle radiogroups properly unless radio is direct child of radiogroup
  const innerRole = Platform.OS === 'web' && accessibilityRole === 'radiogroup' ? accessibilityRole : undefined;
  return /*#__PURE__*/_jsx(Fieldset, {
    ...systemProps,
    ref: ref,
    name: inputGroupName,
    legend: legend,
    tooltip: tooltip,
    hint: hint,
    hintPosition: hintPosition,
    space: fieldSpace,
    feedback: feedback,
    readOnly: readOnly,
    inactive: inactive,
    validation: validation,
    accessibilityRole: accessibilityRole,
    style: {
      borderRadius,
      backgroundColor,
      padding,
      ...(Platform.OS === 'web' ? {
        gap,
        width: variant?.width === 'equal' ? '100%' : 'auto'
      } : {
        alignSelf: 'flex-start'
      })
    },
    ...selectProps(rest),
    children: /*#__PURE__*/_jsx(StackWrap, {
      accessibilityRole: innerRole,
      space: space,
      direction: direction,
      tokens: stackTokens,
      gap: gap,
      ref: ref,
      children: items.map((_ref2, index) => {
        let {
          label,
          id = label,
          accessibilityLabel,
          ref: itemRef,
          ...itemRest
        } = _ref2;
        const isSelected = currentValues.includes(id);

        // Pass an object of relevant component state as first argument for any passed-in press handlers
        const pressHandlers = getPressHandlersWithArgs(rest, [{
          id,
          label,
          currentValues
        }]);
        const handlePress = event => {
          if (pressHandlers.onPress) pressHandlers.onPress(event);
          toggleOneValue(id, event);
        };
        const iconProp = itemRest.icon || undefined;
        const itemA11y = {
          accessibilityState: {
            checked: isSelected
          },
          accessibilityRole: itemA11yRole,
          accessibilityLabel,
          ...a11yProps.getPositionInSet(items.length, index)
        };

        // Ensure button is direct child of group as MacOS voiceover only applies "X of Y" to
        // "radio" if it's a direct child of "radiogroup", even if aria-posinset etc exists
        return /*#__PURE__*/_jsx(ButtonBase, {
          ref: itemRef,
          variant: buttonVariant,
          ...pressHandlers,
          onPress: handlePress,
          tokens: getButtonTokens,
          selected: isSelected,
          inactive: inactive,
          icon: iconProp,
          ...selectItemProps({
            ...itemRest,
            ...itemA11y
          }),
          iconPosition: iconPosition,
          children: label
        }, id);
      })
    })
  });
});
ButtonGroup.displayName = 'ButtonGroup';
ButtonGroup.propTypes = {
  ...selectedSystemPropTypes,
  tokens: getTokensPropType('ButtonGroup'),
  /**
   * To allow Button specific variant
   */
  buttonVariant: variantProp.propType,
  /**
   * Default variant prop
   */
  variant: variantProp.propType,
  /**
   * The maximum number of items a user may select at once. Defaults to 1 and behaves
   * like radio buttons. To have no limit and allow any number of selections, pass `null`.
   */
  maxValues: PropTypes.number,
  /**
   * The options a user may select
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    ...selectedItemPropTypes,
    /**
     * The text displayed to the user in the button, describing this option,
     * passed to the button as its child.
     */
    label: PropTypes.string.isRequired,
    /**
     * An optional accessibility label may be passed to ButtonGroup Buttons
     * which is applied as normal for a React Native accessibilityLabel prop.
     */
    accessibilityLabel: PropTypes.string,
    /**
     * An optional unique string may be provided to identify this option,
     * which will be used in code and passed to any onChange function.
     * If not provided, the label is used.
     */
    id: PropTypes.string,
    /**
     * An optional ref for one individual button in the ButtonGroup
     */
    ref: ABBPropTypes.ref()
  })),
  /**
   * If provided, this function is called when the current selection is changed
   * and is passed an array of the `id`s of all currently selected `items`.
   */
  onChange: PropTypes.func,
  /**
   * If the selected item(s) in the button group are to be controlled externally by
   * a parent component, pass an array of strings as well as an `onChange` handler.
   * Passing an array for "values" makes the ButtonGroup a "controlled" component that
   * expects its state to be handled via `onChange` and so doesn't handle it itself.
   */
  values: PropTypes.arrayOf(PropTypes.string),
  /**
   * If `values` is not passed, making the ButtonGroup an "uncontrolled" component
   * managing its own selected state, a default set of selections may be provided.
   * Changing the `initialValues` does not change the user's selections.
   */
  initialValues: PropTypes.arrayOf(PropTypes.string),
  /**
   * Defines if the icon will be displayed on the right or left side of the label.
   */
  iconPosition: PropTypes.oneOf(['left', 'right']),
  /**
   * Main text used to describe this group, used in Fieldset's Legend element.
   */
  legend: PropTypes.string,
  /**
   * Optional additional text giving more detail to help a user make a choice.
   */
  hint: PropTypes.string,
  /**
   * Position of the hint relative to label. Use `below` to display a larger hint below the label.
   */
  hintPosition: PropTypes.oneOf(['inline', 'below']),
  /**
   * Optional tooltip text content to include alongside the legend and hint.
   */
  tooltip: PropTypes.string,
  /**
   * Current validation status of the group, passed to the feedback element if there is one.
   */
  validation: PropTypes.oneOf(['error', 'success']),
  /**
   * If provided, a Feedback element is rendered containing this text.
   */
  feedback: PropTypes.string,
  /**
   * If true, the buttons cannot be selected by the user and simply show their current state.
   */
  readOnly: PropTypes.bool,
  /**
   * If true, the buttons cannot be interacted with, elements are set as `disabled` and if the
   * theme supports `inactive` appearances rules, these are applied.
   */
  inactive: PropTypes.bool,
  /**
   * On Web, this is passed to the `name` attribute of the fieldset.
   */
  name: PropTypes.string,
  /**
   * Sets the language of microcopy in subcomponents (e.g. Tooltip's default accessibility label).
   */
  copy: PropTypes.oneOf(['en', 'fr'])
};
export default ButtonGroup;